import { isAnalysisMethod, isGroup, isObjectRecord } from "../../typeGuards";
import { isGroupingFactor } from "../../typeGuards/isGroupingFactor";
import {
  ArsTableSubGroup,
  ArsTableSubGroupAnalysisMethod,
  ArsTableSubGroupGroup,
  ArsTableSubGroupGroupingFactor,
} from "../interface";
import { isArsTableGroupCalculationDto } from "./isArsTableGroupCalculationDto";

/**
 * Validates shared ArsTableSubGroup properties that all subgroup variants have.
 *
 * @param obj - The object to check
 * @param isT - A type guard function for the generic type T used in the subgroup label
 * @returns - True if all common subgroup fields are valid
 */
const isArsTableSubGroupBase = <T = string>(
  obj: Record<string, unknown>,
  isT: (value: unknown) => value is T,
): boolean => {
  if (typeof obj.id !== "string") return false;
  if (!["number", "undefined"].includes(typeof obj.dimension)) return false;
  if (!["boolean", "undefined"].includes(typeof obj.hidden)) return false;
  if (!["boolean", "undefined"].includes(typeof obj.spaceAfterGroup)) return false;
  if (!isT(obj.label)) return false;

  if (
    obj.subGroups !== undefined &&
    (!Array.isArray(obj.subGroups) ||
      obj.subGroups.some((subGroup) => !isArsTableGroupCalculationDto(subGroup, isT)))
  )
    return false;

  return true;
};

/**
 * Type guard for ArsTableSubGroupAnalysisMethod.
 *
 * @param obj - The object to check
 * @param isT - A type guard function for the generic type T used in the subgroup label
 * @returns - True if the object is an ArsTableSubGroupAnalysisMethod, false otherwise
 */
export const isArsTableSubGroupAnalysisMethod = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T,
): obj is ArsTableSubGroupAnalysisMethod<T> => {
  if (!isObjectRecord(obj)) return false;
  if (typeof obj.analysisMethodId !== "string") return false;
  if (!isArsTableSubGroupBase(obj, isT)) return false;
  if (!isAnalysisMethod(obj.analysisMethod)) return false;
  if (!Array.isArray(obj.subGroups)) return false;

  return true;
};

/**
 * Type guard for ArsTableSubGroupGroup.
 *
 * @param obj - The object to check
 * @param isT - A type guard function for the generic type T used in the subgroup label
 * @returns - True if the object is an ArsTableSubGroupGroup, false otherwise
 */
export const isArsTableSubGroupGroup = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T,
): obj is ArsTableSubGroupGroup<T> => {
  if (!isObjectRecord(obj)) return false;
  if (typeof obj.groupId !== "string") return false;
  if (!isArsTableSubGroupBase(obj, isT)) return false;
  if (!isGroup(obj.group)) return false;
  if (!["boolean", "undefined"].includes(typeof obj.isDataDriven)) return false;

  return true;
};

/**
 * Type guard for ArsTableSubGroupGroupingFactor.
 *
 * @param obj - The object to check
 * @param isT - A type guard function for the generic type T used in the subgroup label
 * @returns - True if the object is an ArsTableSubGroupGroupingFactor, false otherwise
 */
export const isArsTableSubGroupGroupingFactor = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T,
): obj is ArsTableSubGroupGroupingFactor<T> => {
  if (!isObjectRecord(obj)) return false;
  if (!isGroupingFactor(obj.groupingFactor)) return false;
  if (!isArsTableSubGroupBase(obj, isT)) return false;
  if (typeof obj.groupingFactorId !== "string") return false;
  if (!["boolean", "undefined"].includes(typeof obj.spaceAfterSubGroups)) return false;

  return true;
};

/**
 * Type guard for ArsTableSubGroup, which is a possible type of ArsTableGroup.
 *
 * @param obj - The object to check
 * @param isT - A type guard function for the generic type T used in the subgroup label
 * @returns - True if the object is an ArsTableSubGroup, false otherwise
 */
export const isArsTableSubGroup = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T,
): obj is ArsTableSubGroup<T> =>
  isArsTableSubGroupGroupingFactor(obj, isT) ||
  isArsTableSubGroupGroup(obj, isT) ||
  isArsTableSubGroupAnalysisMethod(obj, isT);
